import { useCallback, useState, useEffect, useRef, useMemo, useSyncExternalStore } from "react";
import { isUndecided } from "../nba/clubs";
import type {
  Game,
  GameDay,
  RosterPlayer,
  TeamRoster,
  DisplayGame,
  StreamStatus,
  RawStandingRow,
  StandingRow,
  StandingsByConference,
  Franchise,
  WireEntry,
  WireType,
  BoxScore,
  GamePlay,
  WinPoint,
  Team,
  PlayerProfile,
  PlayerCareerTable,
  PlayerTotals,
  PlayerGameLog,
  CareerHighs,
  CareerLeaderCategory,
  CareerLeaderRow,
  SeasonLeaderBlock,
  DraftPick,
  TeamContracts,
  PlayerContracts,
  PlayerContractEntry,
  Champion,
  RetiredBanner,
} from "../types/nba";
import type { League as TradeLeague } from "../nba/trade/value";
import type { Snapshot as TradeSnapshot } from "../nba/trade/snapshot";
import { isLocalToday, rememberFirstTips } from "../today";
import { rereadsDay } from "../scrub";
import { clockOpts, zoneOpts } from "../shell/tz";
import { useMasked } from "../shell/spoilers";
import { apiFor, GL_API, NBA_API } from "../league";

/** The NBA API; a league's own reads go through apiFor (DSPRT-474). */
const API: string = NBA_API;

/**
 * The URL of a country's flag, for an <img src>.
 *
 * Exported rather than built at the call site so the one place that knows where
 * the API lives stays the one place. Not a hook and not fetched here: it is an
 * image, the browser's own cache is the right cache for it, and the endpoint
 * answers with a year of `immutable` so a roster of fifteen names asks for each
 * flag once ever.
 */
export function flagUrl(iso: string): string {
  return `${API}/flags/${encodeURIComponent(iso)}`;
}

/**
 * Typed fetch. The generic is an assertion, not a validation — nothing here
 * checks the response actually matches T at runtime. That's a deliberate
 * trade for a project this size, but it means a backend shape change fails
 * silently at the point of use rather than loudly at the boundary. If that
 * ever bites, zod at this one function is the fix.
 */
/**
 * Identical reads in flight share one request (DSPRT-232): two parts of the
 * home page asking for the same schedule range at once made two calls.
 * Dropped once settled, so this is no cache — the browser's is.
 */
const inFlight = new Map<string, Promise<unknown>>();

/**
 * Reads made ahead of a click (DSPRT-611), for the first read of the same
 * address within half a minute. The browser's cache can't be relied on for
 * this: most reads may only be kept a few seconds, some not at all.
 */
const warm = new Map<string, { at: number; value: unknown }>();
const WARM_MS = 30_000;

/** Reads these addresses now, so the dialog or page that wants them next opens on them. */
export function prefetch(paths: string[]): void {
  for (const path of paths) {
    const ahead = warm.get(path);
    if (inFlight.has(path) || (ahead && performance.now() - ahead.at < WARM_MS)) continue;
    get<unknown>(path)
      .then((value) => {
        warm.set(path, { at: performance.now(), value });
        if (warm.size > 60) warm.delete(warm.keys().next().value!);
      })
      .catch(() => {});
  }
}

/** What a player card reads first, as a data-prefetch value. */
export function playerReads(id: string): string {
  const p = `/players/${encodeURIComponent(id)}`;
  return `${p}/bio ${p}/career?rev=2 ${p}/totals`;
}
/** What a game's dialog reads first. */
export function gameReads(id: string): string {
  return `/games/${id}/box-score /games/${id}/plays /games/${id}/win-probability`;
}
/** What a club's card reads first. */
export function teamReads(abbr: string): string {
  return `/teams/${abbr}/roster /teams/${encodeURIComponent(abbr)}/schedule`;
}

/** How long a finger rests before its tap's reads start (B-296), and how far it may wander and still be a tap. */
const TOUCH_MS = 120;
const TOUCH_SLOP = 10;

/**
 * Anything carrying data-prefetch (a player's name, a game card, a club)
 * starts its reads once the pointer has rested on it for a moment, on a
 * tap, or when the keyboard reaches it, so the click opens on data that is
 * already here instead of a loader. Skipped when the reader has asked to save
 * data. Installed once, from main.tsx.
 */
export function installPrefetch(): void {
  const saving = () => !!(navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData;
  const target = (e: Event) => (e.target instanceof Element ? e.target.closest<HTMLElement>("[data-prefetch]") : null);
  const run = (el: HTMLElement | null) => {
    const paths = el?.dataset.prefetch?.split(" ").filter(Boolean);
    if (paths?.length && !saving()) prefetch(paths);
  };
  let armed: HTMLElement | null = null;
  let timer = 0;
  document.addEventListener("pointerover", (e) => {
    if ((e as PointerEvent).pointerType === "touch") return;
    const el = target(e);
    if (el === armed) return;
    window.clearTimeout(timer);
    armed = el;
    if (el) timer = window.setTimeout(() => run(el), 90);
  }, { passive: true });
  // A touch warms the reads only once it is plainly a tap (B-296): a finger
  // that rests TOUCH_MS, or lifts first. A scroll starts with a touchstart
  // too, and every card a swipe began on used to download its box score,
  // plays and win probability.
  let touched: HTMLElement | null = null;
  let touchTimer = 0;
  let touchX = 0, touchY = 0;
  const dropTouch = () => {
    window.clearTimeout(touchTimer);
    touched = null;
  };
  const settleTouch = () => {
    const el = touched;
    dropTouch();
    run(el);
  };
  document.addEventListener("touchstart", (e) => {
    dropTouch();
    const el = target(e);
    if (!el || e.touches.length !== 1) return;
    touched = el;
    touchX = e.touches[0].clientX;
    touchY = e.touches[0].clientY;
    touchTimer = window.setTimeout(settleTouch, TOUCH_MS);
  }, { passive: true });
  document.addEventListener("touchmove", (e) => {
    const t = e.touches[0];
    if (touched && (!t || Math.hypot(t.clientX - touchX, t.clientY - touchY) > TOUCH_SLOP)) dropTouch();
  }, { passive: true });
  document.addEventListener("touchend", () => { if (touched) settleTouch(); }, { passive: true });
  document.addEventListener("touchcancel", dropTouch, { passive: true });
  // The page's own scroll only: the score strip's tape scrolls all the time.
  window.addEventListener("scroll", dropTouch, { passive: true });
  document.addEventListener("focusin", (e) => run(target(e)));
}

/**
 * `fresh`: past the browser's cache, for the re-reads of a live game (DSPRT-552).
 * The server lets a copy be kept a few seconds, and a re-read sent before
 * that ran out was answered from the browser's copy, so every other one
 * brought nothing new.
 */
async function get<T>(path: string, fresh = false): Promise<T> {
  // Read ahead while the pointer rested on the link (DSPRT-611): taken once.
  if (!fresh) {
    const ahead = warm.get(path);
    if (ahead) {
      warm.delete(path);
      if (performance.now() - ahead.at < WARM_MS) return ahead.value as T;
    }
  }
  const pending = inFlight.get(path);
  // A copy for the second caller, so neither can sort the other's rows.
  if (pending) return (pending as Promise<T>).then((v) => structuredClone(v));
  const p = (async () => {
    const res = await fetch(`${apiFor(path)}${path}`, fresh ? { cache: "no-store" } : undefined);
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    // 204: nothing there yet, such as a box score before tip-off (B-157). The
    // caller sees the same failure as a 404, without the console's network error.
    if (res.status === 204) throw new Error("204 No Content");
    return (await res.json()) as T;
  })();
  inFlight.set(path, p);
  p.finally(() => inFlight.delete(path)).catch(() => {});
  return p;
}

/**
 * The viewer's own calendar day a game tips off on, yyyy-MM-dd — not always
 * `date`, which is the league's (Eastern) game day: a 7 PM ET tip is 2 AM the
 * next day in Tel Aviv. Falls back to `date` when the tip-off isn't known.
 */
export function localDay(g: Pick<Game, "tipoff" | "date">): string {
  return g.tipoff ? new Date(g.tipoff).toLocaleDateString("en-CA", zoneOpts()) : g.date;
}

/** One empty list, so a hook with nothing yet hands back the same thing each render. */
const NO_GAMES: DisplayGame[] = [];

/** Adds the fields the card components need but the API doesn't send. */
export function adapt(g: Game): DisplayGame {
  // A slot whose sides aren't decided has no tip-off time either: ESPN files it at midnight Eastern (#123).
  if (isUndecided(g)) return { ...g, clock: g.clockSeconds ?? 0, tip: "TBD", tipTime: "TBD" };
  const time = g.tipoff
    ? new Date(g.tipoff).toLocaleTimeString("en-US", clockOpts())
    : "";
  // The cards are filed under the league's day (DSPRT-212): when the viewer's
  // clock has already rolled into the next one, the time says which day it is
  // on, so a card under "Today" never reads "2:00 AM" for tomorrow morning.
  const day = time && localDay(g) !== g.date && !isLocalToday(g.tipoff)
    ? new Date(g.tipoff as string).toLocaleDateString("en-US", { weekday: "short", ...zoneOpts() }) + " "
    : "";
  return {
    ...g,
    clock: g.clockSeconds ?? 0,
    tip: day + time,
    tipTime: time,
  };
}

export interface UseScoreboardResult {
  games: DisplayGame[];
  status: StreamStatus;
  updatedAt: Date | null;
}

/**
 * Today's scoreboard, seeded by a REST call then kept current over SSE.
 *
 * Updates land roughly every 5 seconds during live games — close enough to
 * real-time to call it live. That cadence is a courtesy interval chosen in
 * ScoreboardService, not an external rate limit.
 *
 * @param date ISO yyyy-MM-dd for a past slate, or null for today. Past dates
 *             are frozen server-side, so no stream is opened for them.
 * @param ready false while the caller still doesn't know which day it wants.
 *              Without it the first render passes null — read as "today" — and
 *              opens an EventSource that the next render immediately closes
 *              once the newest game day resolves to a past date. The connection
 *              was torn down correctly, but it showed up as an aborted /stream
 *              request on every load and cost a needless round trip.
 */
/**
 * Cache-buster for past-date scoreboards, which are served immutable for a
 * year. Bump it whenever the shape of a past game changes: 2 added `top`,
 * 3 added `top.rating`.
 */
export const PAST_DAY_VERSION = 3;

interface StreamSubscriber {
  frame: (games: Game[]) => void;
  error: () => void;
}

/*
 * One /stream connection per tab (DSPRT-398, #137). The score strip and the
 * page's own scoreboard each call useScoreboard(), and each used to open its
 * own EventSource: two long-lived connections per open tab. They now share
 * one, opened by the first subscriber and closed when the last one leaves.
 * A late subscriber gets the newest frame at once instead of waiting for the
 * next one.
 */
const streamSubs = new Set<StreamSubscriber>();
let sharedStream: EventSource | null = null;
let lastFrame: Game[] | null = null;

function subscribeStream(sub: StreamSubscriber): () => void {
  streamSubs.add(sub);
  if (!sharedStream) {
    const es = new EventSource(`${apiFor("/stream")}/stream`);
    es.addEventListener("scoreboard", (evt: MessageEvent<string>) => {
      let parsed: Game[];
      try {
        parsed = JSON.parse(evt.data) as Game[];
      } catch {
        return; /* malformed frame; the next one will be fine */
      }
      lastFrame = parsed;
      streamSubs.forEach((s) => s.frame(parsed));
    });
    es.onerror = () => streamSubs.forEach((s) => s.error());
    sharedStream = es;
  } else if (lastFrame) {
    sub.frame(lastFrame);
  }
  return () => {
    streamSubs.delete(sub);
    if (streamSubs.size === 0 && sharedStream) {
      sharedStream.close();
      sharedStream = null;
      lastFrame = null;
    }
  };
}

/**
 * One live game's frame from the score stream, for its open card (DSPRT-558):
 * null until the stream has said anything about it, or while `gameId` is null.
 */
export function useStreamGame(gameId: string | null): Game | null {
  const [game, setGame] = useState<Game | null>(null);
  useEffect(() => {
    setGame(null);
    if (!gameId) return;
    return subscribeStream({
      frame: (parsed) => setGame(parsed.find((g) => g.id === gameId) ?? null),
      error: () => {},
    });
  }, [gameId]);
  return game;
}

/*
 * The newest moment an open game card has (DSPRT-558): its box score and plays
 * can be ahead of the score stream, and the strip on the page behind it should
 * not show an older score than the card does. Only while the card is open.
 */
export interface LiveMoment { period: number; left: number; atBreak?: boolean; away: number | null; home: number | null }
const liveMoments = new Map<string, LiveMoment>();
const momentListeners = new Set<() => void>();
let momentsVersion = 0;

export function publishLiveMoment(gameId: string, moment: LiveMoment | null): void {
  const was = liveMoments.get(gameId);
  if (!moment && !was) return;
  if (moment && was && moment.period === was.period && moment.left === was.left && moment.atBreak === was.atBreak && moment.away === was.away && moment.home === was.home) return;
  if (moment) liveMoments.set(gameId, moment);
  else liveMoments.delete(gameId);
  momentsVersion++;
  momentListeners.forEach((l) => l());
}

function subscribeMoments(l: () => void): () => void {
  momentListeners.add(l);
  return () => { momentListeners.delete(l); };
}

/** A stream game brought up to its open card's moment, when the card is further on. */
function withMoment(g: DisplayGame): DisplayGame {
  const m = liveMoments.get(g.id);
  if (!m || g.state !== "LIVE") return g;
  const at = (period: number, left: number) => (period <= 4 ? period * 720 : 2880 + (period - 4) * 300) - left;
  // A period under way with its clock at zero has lost its clock (DSPRT-627): it counts from the period's start.
  const streamAt = g.clockSeconds <= 0 && g.atBreak === false ? at(g.period, g.period <= 4 ? 720 : 300) : at(g.period, g.clockSeconds);
  const ahead = at(m.period, m.left) > streamAt;
  const top = (a: number | null, b: number | null) => (a == null ? b : b == null ? a : Math.max(a, b));
  const away = top(g.away.score, m.away), home = top(g.home.score, m.home);
  if (!ahead && away === g.away.score && home === g.home.score) return g;
  return {
    ...g,
    ...(ahead ? { period: m.period, clockSeconds: m.left, clock: m.left, atBreak: m.left <= 0 && m.atBreak !== false } : {}),
    away: { ...g.away, score: away },
    home: { ...g.home, score: home },
  };
}

/** Between two reads of a date still being played (B-280); the server lets a copy be kept 10 s. */
const REREAD_MS = 15_000;

export function useScoreboard(date: string | null = null, ready = true): UseScoreboardResult {
  const [games, setGames] = useState<DisplayGame[]>([]);
  const [status, setStatus] = useState<StreamStatus>("loading");
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    // `v` is a cache-buster, not a parameter the server reads. A past date is
    // served with a year-long immutable Cache-Control, so a browser that
    // fetched one before games carried `top` would otherwise keep the old copy
    // for a year. Bump it whenever the shape of a past game changes.
    const path = date ? `/scoreboard?date=${date}&v=${PAST_DAY_VERSION}` : "/scoreboard";
    // A date is read once, unless it is the league night after midnight with
    // a game still being played (B-280): that is re-read, past the browser's
    // copy, until every game on it is over. The stream has moved on to the
    // new date by then, so nothing else would bring its score.
    let timer = 0;
    const reread = () => {
      timer = window.setTimeout(() => {
        get<Game[]>(path, true)
          .then((data) => {
            if (cancelled) return;
            setGames(data.map(adapt));
            setUpdatedAt(new Date());
            if (rereadsDay(date!, data)) reread();
          })
          .catch(() => {
            if (!cancelled) reread();
          });
      }, REREAD_MS);
    };

    get<Game[]>(path)
      .then((data) => {
        if (cancelled) return;
        setGames(data.map(adapt));
        setUpdatedAt(new Date());
        setStatus("live");
        if (date && rereadsDay(date, data)) reread();
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });

    if (date) {
      return () => {
        cancelled = true;
        window.clearTimeout(timer);
      };
    }

    const unsubscribe = subscribeStream({
      frame: (parsed) => {
        setGames(parsed.map(adapt));
        setUpdatedAt(new Date());
        setStatus("live");
      },
      // EventSource retries on its own with backoff. Surfacing the state lets
      // the UI show a hint instead of silently going stale.
      error: () => setStatus("polling"),
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [date, ready]);

  const moments = useSyncExternalStore(subscribeMoments, () => momentsVersion);
  // `moments` is the store's version: a card's newer moment redraws the strip.
  const synced = useMemo(() => (liveMoments.size ? games.map(withMoment) : games), [games, moments]);
  return { games: useMasked(synced), status, updatedAt };
}

/**
 * Flashes a team's score when it changes, by diffing each SSE frame against
 * the previous one.
 *
 * @returns the abbreviation of the team whose score just changed, or null.
 */
export function useScoreFlash(games: DisplayGame[]): string | null {
  const [flash, setFlash] = useState<string | null>(null);
  const prev = useRef<Record<string, number | null>>({});

  useEffect(() => {
    const next: Record<string, number | null> = {};
    let changed: string | null = null;

    for (const g of games) {
      for (const side of [g.home, g.away]) {
        // Keyed by GAME id, not team abbreviation alone. Keyed by abbr, moving
        // the scrubber to another date compared a team's score in one game
        // against its score in a completely different game — the Finals are
        // the same two teams every night — and the mismatch read as a live
        // score change, so the top row flashed red on the first click.
        const key = `${g.id}:${side.abbr}`;
        next[key] = side.score;
        const before = prev.current[key];
        // `!= null` intentionally catches undefined too — a score seen for the
        // first time must not flash, only one that actually moved. A new date
        // brings new game ids, so every key is unseen and nothing flashes.
        if (before != null && before !== side.score) changed = key;
      }
    }
    prev.current = next;

    if (!changed) return;
    setFlash(changed);
    const t = window.setTimeout(() => setFlash(null), 800);
    return () => window.clearTimeout(t);
  }, [games]);

  return flash;
}

export interface UseStandingsResult {
  standings: StandingsByConference;
  loading: boolean;
  /** The table's season as the server names it, "2025-26"; null until it lands. */
  season: string | null;
  /**
   * The records to judge clubs by: this season's once a game is played,
   * last season's final table before (DSPRT-233 made this season's table
   * 0-0 from 1 October, where it used to stay last season's until opening
   * night). For the home page's picks and the Trade Machine's outlooks.
   */
  form: StandingsByConference;
}

/** Coerced rows: every numeric field arrives as a string (ESPN's displayValue). */
export function coerceStandings(rows: RawStandingRow[]): StandingRow[] {
  return rows.map((r) => ({
    ...r,
    wins: Number(r.wins) || 0,
    losses: Number(r.losses) || 0,
    rank: Number(r.rank) || 0,
  }));
}

function byConference(rows: StandingRow[]): StandingsByConference {
  return {
    East: rows.filter((r) => r.conference === "East").sort((a, b) => a.rank - b.rank),
    West: rows.filter((r) => r.conference === "West").sort((a, b) => a.rank - b.rank),
  };
}

/** "2025-26" → "2024-25". */
function previousSeason(label: string): string | null {
  const m = /^(\d{4})-\d{2}$/.exec(label);
  if (!m) return null;
  const start = Number(m[1]) - 1;
  return `${start}-${String((start + 1) % 100).padStart(2, "0")}`;
}

async function fetchStandings(season?: string | null, reload = false): Promise<{ rows: StandingRow[]; label: string | null }> {
  const res = await fetch(`${apiFor("/standings")}/standings${season ? `?season=${encodeURIComponent(season)}` : ""}`,
    reload ? { cache: "reload" } : undefined);
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  const raw = (await res.json()) as RawStandingRow[];
  // A table from before divisions were sent (DSPRT-233) is the browser's
  // cached copy of an older response (they're kept 15 minutes): ask again
  // past the cache rather than show a division view with no divisions.
  if (!reload && raw.length > 0 && raw.every((r) => r.division === undefined)) return fetchStandings(season, true);
  // So is a past season's whose conference records are all 0-0 though it was
  // played: ESPN's 1993-94 to 2002-03 tables came that way until DSPRT-237,
  // and a past season is kept a day.
  if (!reload && season && raw.some((r) => Number(r.wins) + Number(r.losses) > 0)
      && raw.every((r) => r.confRecord == null || r.confRecord === "0-0")
      && raw.some((r) => r.confRecord === "0-0")) return fetchStandings(season, true);
  return { rows: coerceStandings(raw), label: res.headers.get("X-Season") };
}

/**
 * Standings, split by conference and sorted by playoff seed.
 *
 * Every numeric field arrives as a string (the backend reads ESPN's
 * `displayValue`, which is pre-formatted text), so coercion happens here
 * rather than leaking `"41" - "11"` bugs into the components.
 */
export function useStandings(enabled = true): UseStandingsResult {
  const [standings, setStandings] = useState<StandingsByConference>({ East: [], West: [] });
  const [form, setForm] = useState<StandingsByConference>({ East: [], West: [] });
  const [loading, setLoading] = useState(true);
  // Whatever season the newest table is, named by the server (DSPRT-227):
  // this used to ask for "2025-26" and say so over a table that, from
  // opening night on, would be 2026-27's.
  const [season, setSeason] = useState<string | null>(null);

  useEffect(() => {
    // Lifted to the root so the team modal can read a record from any section,
    // but still gated: someone who only ever looks at scores should not pay for
    // a standings fetch.
    if (!enabled) return;
    let cancelled = false;

    fetchStandings()
      .then(async ({ rows, label }) => {
        if (cancelled) return;
        setSeason(label);
        const table = byConference(rows);
        setStandings(table);
        const unplayed = rows.every((r) => r.wins + r.losses === 0);
        const prev = unplayed && label ? previousSeason(label) : null;
        if (!prev) {
          setForm(table);
          return;
        }
        const last = await fetchStandings(prev).catch(() => null);
        if (!cancelled) setForm(last ? byConference(last.rows) : table);
      })
      .catch(() => {
        /* fall back to fixtures in the component */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { standings, loading, season, form };
}

export interface UseSeasonStandingsResult {
  /** Every club, in table order within its conference (or division, before 1970-71). */
  rows: StandingRow[];
  /** "1979-80", as the server named it. */
  season: string | null;
  loading: boolean;
  error: boolean;
}

/**
 * One past season's table (DSPRT-233), any back to 1946-47; `season` is its
 * closing year (1980 = 1979-80), null for none. Each is fetched once a visit.
 */
const seasonTables = new Map<number, { rows: StandingRow[]; label: string | null }>();
export function useSeasonStandings(season: number | null): UseSeasonStandingsResult {
  const cached = season != null ? seasonTables.get(season) : undefined;
  const [state, setState] = useState<{ for: number | null; rows: StandingRow[]; label: string | null; error: boolean }>(
    { for: null, rows: [], label: null, error: false },
  );

  useEffect(() => {
    if (season == null || seasonTables.has(season)) return;
    let cancelled = false;
    fetchStandings(String(season))
      .then((t) => {
        seasonTables.set(season, t);
        if (!cancelled) setState({ for: season, rows: t.rows, label: t.label, error: false });
      })
      .catch(() => {
        if (!cancelled) setState({ for: season, rows: [], label: null, error: true });
      });
    return () => {
      cancelled = true;
    };
  }, [season]);

  if (season == null) return { rows: [], season: null, loading: false, error: false };
  if (cached) return { rows: cached.rows, season: cached.label, loading: false, error: false };
  const mine = state.for === season;
  return { rows: mine ? state.rows : [], season: mine ? state.label : null, loading: !mine, error: mine && state.error };
}

/**
 * Franchise history. Served from the hand-authored `reference_data` seed, so
 * the shape already matches what the UI wants — no mapping needed.
 */
export function useFranchises(enabled = true): Franchise[] {
  const [rows, setRows] = useState<Franchise[]>([]);

  useEffect(() => {
    // Same gating as useStandings: the All-Time table and the team modal both
    // want this, nothing else does.
    if (!enabled) return;
    let cancelled = false;
    get<Franchise[]>("/franchises")
      .then((r) => !cancelled && setRows(r))
      .catch(() => !cancelled && setRows([]));
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return rows;
}

/**
 * All-time top ten of each requested career category — backs the All-Time
 * page's player leaders (DSPRT-44). `enabled` gates the fetch the same way
 * useFranchises does: only the All-Time page wants this, and a cold call
 * can be slow (each never-before-seen leader costs its own athlete lookup
 * server-side), so nothing should pay for it just by being mounted.
 */
/**
 * Every player in the all-time candidate pool, each with their full career
 * line in both readings — per-game averages and raw totals — DSPRT-44's
 * single sortable, totals/averages-toggled list. One fetch, cached
 * server-side: every column either mode can sort by, and both modes
 * themselves, are already in each row, so switching the sort picker OR the
 * toggle is a client-side re-sort, not a new request.
 */
export function useCareerLeaders(
  enabled = true,
): { leaders: CareerLeaderRow[]; averageColumns: string[]; totalColumns: string[]; loading: boolean; retry: () => void } {
  const [leaders, setLeaders] = useState<CareerLeaderRow[]>([]);
  const [averageColumns, setAverageColumns] = useState<string[]>([]);
  const [totalColumns, setTotalColumns] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  // Bumped by "Try again" once the polls below have given up (DSPRT-423).
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let full = false;
    let failures = 0;
    let retry: ReturnType<typeof setTimeout> | undefined;
    setLoading(true);

    const apply = (data: CareerLeaderCategory) => {
      if (cancelled) return;
      setLeaders(data.rows);
      setAverageColumns(data.averageColumns);
      setTotalColumns(data.totalColumns);
      setLoading(false);
    };

    // `rev` is ignored by the backend. It only changes the URL, so browsers
    // holding a day-cached copy (Cache-Control max-age 1 day) fetch the
    // corrected list at once. Bump it when the list's data changes shape
    // or meaning, as with the Basketball-Reference corrections.
    //
    // The full pool is slow to answer cold: each never-before-seen player in
    // it costs its own athlete lookup server-side. depth=50 comes back with
    // what the page needs for its first paint; polling for the full list
    // behind it fills in the rest once the server-side cache has warmed,
    // instead of the page sitting on a spinner for however long that takes.
    get<CareerLeaderCategory>("/career-leaders?depth=50&rev=6")
      .then(apply)
      .catch(() => {
        if (cancelled) return;
        setLeaders([]);
        setLoading(false);
      });

    const pollFull = () => {
      get<CareerLeaderCategory>("/career-leaders?depth=100&rev=6")
        .then((data) => {
          if (cancelled) return;
          full = true;
          apply(data);
        })
        .catch(() => {
          failures += 1;
        })
        .finally(() => {
          // Backing off, then stopping (DSPRT-423): a poll every five seconds
          // for as long as the tab stayed open kept a slow server slower.
          const wait = FULL_LIST_RETRIES[failures - 1];
          if (!cancelled && !full && wait != null) retry = setTimeout(pollFull, wait);
        });
    };
    pollFull();

    return () => {
      cancelled = true;
      if (retry) clearTimeout(retry);
    };
  }, [enabled, attempt]);

  return { leaders, averageColumns, totalColumns, loading, retry: () => setAttempt((n) => n + 1) };
}

/** Waits before each new try at the full All-Time list after a failure; none left, it stops. */
const FULL_LIST_RETRIES = [5_000, 15_000, 60_000];

/**
 * Transactions and injuries.
 *
 * No mapping layer: the server sends WireEntry in exactly this shape. Age and
 * freshness are deliberately NOT included in the payload — this endpoint is
 * cached for two minutes, so a server-computed "3h ago" would go stale inside
 * the cache window. Derived at render time from `occurredAt` instead.
 */
/**
 * Fetches one bounded batch and lets the caller page through it client-side.
 *
 * `limit` is the size of that batch, NOT the page size — the wire holds a few
 * hundred rows at most, so pulling them once and slicing locally makes page
 * changes instant and avoids a refetch (and a visible flicker) on every click.
 * The server caps this at 200 regardless of what's asked for.
 */
/**
 * Dates that actually have games, newest first, straight from the API.
 *
 * Not the last N calendar days — out of season those are all empty, and a
 * scrubber of dead tabs is worse than no scrubber. Mirrors how ESPN's own
 * scores page walks back to real game days.
 */
/**
 * Box score for one game, fetched on demand.
 *
 * `gameId` null means "no box score open" — the hook clears and skips the
 * request entirely, so opening and closing the panel doesn't leave a stale
 * board behind or fire a request nobody asked for.
 */
/**
 * The 30 franchises, keyed by abbreviation, fetched once.
 *
 * Returned as a Map because every consumer looks teams up by the abbr it
 * already has on a Side or a standings row — an array would just make each
 * caller build this itself. Empty until the fetch lands, so callers must
 * tolerate a miss and fall back to the tricode.
 */
/**
 * Bumped whenever the Team shape gains a field.
 *
 * /teams is sent with `Cache-Control: max-age=43200` because the thirty
 * franchises change about once a decade — correct for the DATA, wrong for a
 * change of SHAPE. When the colour fields were added, every browser holding a
 * cached copy kept serving the old one for up to twelve hours, so a freshly
 * deployed client rendered team-coloured modals with no team colours in them
 * and nothing looked broken enough to investigate.
 *
 * Changing the URL changes the cache key, so a client that knows about a new
 * field cannot be served a response from before it existed. Bump this in the
 * same commit as any new Team field.
 */
const TEAMS_SHAPE = 2;

export function useTeams(): Map<string, Team> {
  const [teams, setTeams] = useState<Map<string, Team>>(new Map());

  useEffect(() => {
    let cancelled = false;
    get<Team[]>(`/teams?shape=${TEAMS_SHAPE}`)
      .then((rows) => {
        if (cancelled) return;
        setTeams(new Map(rows.map((t) => [t.abbr, t])));
      })
      .catch(() => {
        /* leave the map empty — every consumer falls back to the tricode */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return teams;
}

/**
 * `reloadKey` reads the box score again whenever it changes — a preview
 * noticing its game has tipped off (DSPRT-178) after the first read 404'd.
 */
export function useBoxScore(gameId: string | null, reloadKey: string | null = null): {
  box: BoxScore | null;
  loading: boolean;
  error: boolean;
} {
  const [box, setBox] = useState<BoxScore | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!gameId) {
      setBox(null);
      setError(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    let timer = 0;
    setLoading(true);
    setError(false);
    // Clear immediately: without this the previous game's board stays on
    // screen while the next one loads, which reads as wrong data rather than
    // as loading.
    setBox(null);

    const load = (first: boolean) =>
      get<BoxScore>(`/games/${gameId}/box-score`, !first)
        .then((data) => {
          if (cancelled) return;
          setBox(data);
          if (first) setLoading(false);
          // A game in progress is read again while the modal is open (DSPRT-170).
          if (data.status && !isFinalStatus(data.status) && !timer) {
            timer = window.setInterval(() => { if (!document.hidden) load(false); }, LIVE_FEED_MS);
          } else if (timer && (!data.status || isFinalStatus(data.status))) {
            window.clearInterval(timer);
            timer = 0;
          }
        })
        .catch(() => {
          if (cancelled || !first) return;
          setError(true);
          setLoading(false);
        });
    load(true);

    return () => {
      cancelled = true;
      if (timer) window.clearInterval(timer);
    };
  }, [gameId, reloadKey]);

  return { box, loading, error };
}

/** How often an open live game re-reads its box score. */
export const LIVE_FEED_MS = 8_000;
/**
 * How often an open live game re-reads its plays and win probability: the
 * newest plays come from ESPN's faster feed and are kept two seconds on the
 * server (DSPRT-552), so the court is never more than a few seconds behind it.
 */
export const LIVE_PLAYS_MS = 3_000;

/** ESPN's short status for a finished game: "Final", "Final/OT". */
export const isFinalStatus = (status: string) => /^final/i.test(status);

/**
 * A game's full play-by-play and win-probability series (DSPRT-170). Both come
 * from one backend read; either is empty for a game that has not tipped off,
 * and win probability is empty for every preseason game. While `live`, they
 * are read again every LIVE_PLAYS_MS without clearing what is on screen.
 */
export function useGameFeed(gameId: string | null, live: boolean): {
  plays: GamePlay[];
  win: WinPoint[];
  loaded: boolean;
} {
  const [feed, setFeed] = useState<{ id: string; plays: GamePlay[]; win: WinPoint[] } | null>(null);
  useEffect(() => {
    if (!gameId) { setFeed(null); return; }
    let cancelled = false;
    const load = (fresh: boolean) =>
      Promise.all([
        // A bare list from both leagues; the G League once sent the whole record ({plays, ...}) and a cached copy may still (GL-01).
        get<GamePlay[] | { plays?: GamePlay[] }>(`/games/${gameId}/plays`, fresh)
          .then((r) => (Array.isArray(r) ? r : Array.isArray(r?.plays) ? r.plays : []))
          .catch(() => [] as GamePlay[]),
        get<WinPoint[]>(`/games/${gameId}/win-probability`, fresh).catch(() => [] as WinPoint[]),
      ]).then(([plays, win]) => { if (!cancelled) setFeed({ id: gameId, plays, win }); });
    load(live);
    const t = live ? window.setInterval(() => { if (!document.hidden) load(true); }, LIVE_PLAYS_MS) : 0;
    return () => { cancelled = true; if (t) window.clearInterval(t); };
  }, [gameId, live]);
  const mine = feed && feed.id === gameId ? feed : null;
  return { plays: mine?.plays ?? [], win: mine?.win ?? [], loaded: mine != null };
}

/**
 * Several finished games' box scores at once, keyed by game id, for the home
 * carousel's results slide (DSPRT-150). A game whose box fails is simply
 * absent, and the slide draws it from the scoreboard alone.
 */
export function useBoxScores(gameIds: string[], pollMs = 0): Map<string, BoxScore> {
  const key = gameIds.join(",");
  const [boxes, setBoxes] = useState<Map<string, BoxScore>>(() => new Map());
  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    const load = () => {
      for (const id of key.split(",")) {
        get<BoxScore>(`/games/${id}/box-score`)
          .then((box) => { if (!cancelled) setBoxes((m) => new Map(m).set(id, box)); })
          .catch(() => {});
      }
    };
    load();
    // Games in progress (DSPRT-169): read again while they're on.
    const t = pollMs > 0 ? window.setInterval(() => { if (!document.hidden) load(); }, pollMs) : 0;
    return () => { cancelled = true; if (t) window.clearInterval(t); };
  }, [key, pollMs]);
  return boxes;
}

/**
 * Game days, newest first, each carrying the teams that played.
 *
 * `loaded` is separate from an empty array on purpose: "no game days yet"
 * and "no game days at all" look identical in the data but need different
 * screens, and without the flag the scores page flashes its empty state
 * during the first paint.
 */
export function useGameDays(): { days: GameDay[]; loaded: boolean } {
  const [days, setDays] = useState<GameDay[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let retry = 0;
    // A failure is not an answer (#122): it used to count as "loaded, no
    // days", which sent Scores to whatever day it could still find. The list
    // is asked for again, 5 seconds on and then every 15, until it arrives:
    // a server restart is over in about 20.
    let tries = 0;
    const load = () => get<GameDay[]>("/game-days")
      .then((rows) => {
        if (cancelled) return;
        // Defensive: this endpoint used to return bare date strings, and a
        // stale cached response or an old backend would otherwise crash the
        // page on row.date.
        const list = (Array.isArray(rows) ? rows : []).map((r): GameDay =>
          typeof r === "string" ? { date: r, teams: [] } : { date: r.date, teams: r.teams ?? [], first: r.first ?? null },
        );
        // Before the days reach any page: a slate's day on the reader's calendar is its first tip-off's (DSPRT-405, #163).
        rememberFirstTips(list);
        setDays(list);
        setLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        retry = window.setTimeout(load, tries++ === 0 ? 5_000 : 15_000);
      });
    load();
    return () => {
      cancelled = true;
      window.clearTimeout(retry);
    };
  }, []);

  return { days, loaded };
}

export function useWire(
  type: WireType | "ALL" = "ALL",
  limit = 200,
  team: string | null = null,
  /** False skips the request entirely, for a caller whose page is not showing the list. */
  enabled = true,
): { entries: WireEntry[]; loading: boolean; more: boolean; loadMore: () => void; failed: boolean } {
  const [entries, setEntries] = useState<WireEntry[]>([]);
  // The first batch did not answer: an empty list is then "not known", not "nothing filed" (B-271).
  const [failed, setFailed] = useState(false);
  // Starts true, not false. An empty list and a list that has not arrived
  // yet are the same value, so a caller that only sees `entries` renders its
  // empty state on the first frame and corrects a second later — which is
  // what the team modal's "Nothing on the wire for this team." was doing.
  const [loading, setLoading] = useState(true);
  // A full batch means there may be older ones (DSPRT-227): the Wire page
  // used to stop at the newest 200, about ten days of moves.
  const [more, setMore] = useState(false);
  const fetching = useRef(false);
  // Bumped on every new query, so a batch for the old one is dropped.
  const generation = useRef(0);

  const params = useCallback((cursor?: string) => {
    const p = new URLSearchParams({ limit: String(limit) });
    if (type !== "ALL") p.set("type", type);
    // Filtered server-side rather than over the fetched batch: `limit` caps
    // what comes back, so filtering locally would only ever search the most
    // recent 200 entries and silently miss a team's older moves as the wire
    // grows across a season.
    if (team) p.set("team", team);
    if (cursor) p.set("cursor", cursor);
    return p;
  }, [type, limit, team]);

  useEffect(() => {
    if (!enabled) return;
    const gen = ++generation.current;
    fetching.current = false;
    setLoading(true);
    setMore(false);
    setFailed(false);

    get<WireEntry[]>(`/wire?${params()}`)
      .then((rows) => {
        if (gen !== generation.current) return;
        setEntries(rows);
        setMore(rows.length >= limit);
        setLoading(false);
      })
      .catch(() => {
        if (gen !== generation.current) return;
        setEntries([]);
        setFailed(true);
        setLoading(false);
      });
  }, [params, limit, enabled]);

  const loadMore = useCallback(() => {
    if (!more || fetching.current || entries.length === 0) return;
    const last = entries[entries.length - 1];
    const gen = generation.current;
    fetching.current = true;
    // The last row's time and id: rows filed at one timestamp continue on the next page.
    get<WireEntry[]>(`/wire?${params(`${last.occurredAt}~${last.id}`)}`)
      .then((rows) => {
        if (gen !== generation.current) return;
        setEntries((prev) => {
          const seen = new Set(prev.map((e) => e.id));
          return prev.concat(rows.filter((e) => !seen.has(e.id)));
        });
        setMore(rows.length >= limit);
      })
      .catch(() => {
        if (gen === generation.current) setMore(false);
      })
      .finally(() => {
        if (gen === generation.current) fetching.current = false;
      });
  }, [more, entries, params, limit]);

  return { entries, loading, more, loadMore, failed };
}

/**
 * A team's roster, projected from ESPN's raw payload.
 *
 * The endpoint is an unprojected pass-through — roughly 80KB per team, nearly
 * all of it fields no UI reads (contract terms, four link variants per player,
 * college logos in five sizes). Everything below narrows it to the dozen
 * fields the team modal renders.
 *
 * This projection really belongs on the server next to BoxScore's, which
 * already does exactly this for the same reason. Doing it client-side still
 * ships every wasted byte over the wire; it just stops them reaching the
 * components.
 *
 * Nothing here trusts the shape. It's third-party JSON reached through a
 * pass-through, so every access is optional-chained and every field defaults
 * to null — a roster with an unlisted jersey number renders a dash rather
 * than taking the modal down.
 */
function projectRoster(raw: unknown): TeamRoster {
  const r = (raw ?? {}) as Record<string, any>;
  const athletes: any[] = Array.isArray(r.athletes) ? r.athletes : [];

  const players: RosterPlayer[] = athletes.map((a) => ({
    id: String(a?.id ?? a?.uid ?? a?.fullName ?? Math.random()),
    name: a?.fullName ?? a?.displayName ?? "Unknown",
    jersey: a?.jersey ?? null,
    position: a?.position?.abbreviation ?? null,
    height: a?.displayHeight ?? null,
    weight: a?.displayWeight ?? null,
    age: typeof a?.age === "number" ? a.age : null,
    // 0 is a rookie — a real value, and `?? null` keeps it rather than
    // letting a falsy check turn it into "unknown".
    experience: typeof a?.experience?.years === "number" ? a.experience.years : null,
    college: a?.college?.shortName ?? a?.college?.name ?? null,
    headshotUrl: a?.headshot?.href ?? null,
    injury: a?.injuries?.[0]?.status ?? null,
    // Structured here, unlike on the player profile — the roster payload gives
    // city/state/country as separate fields rather than one display string.
    birthCountry: a?.birthPlace?.country ?? null,
  }));

  // Jersey numbers are strings and include "00", which sorts before "0"
  // numerically but not lexically. Unlisted numbers go last rather than
  // sorting as zero.
  players.sort((x, y) => {
    const nx = x.jersey === null ? Infinity : Number(x.jersey);
    const ny = y.jersey === null ? Infinity : Number(y.jersey);
    if (nx !== ny) return nx - ny;
    return x.name.localeCompare(y.name);
  });

  const coach = r.coach?.[0];

  return {
    season: r.season?.displayName ?? null,
    coach: coach ? [coach.firstName, coach.lastName].filter(Boolean).join(" ") || null : null,
    players,
  };
}

export function useRoster(abbr: string | null): {
  roster: TeamRoster | null;
  loading: boolean;
  error: boolean;
} {
  const [roster, setRoster] = useState<TeamRoster | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!abbr) {
      setRoster(null);
      setError(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    setRoster(null);

    get<unknown>(`/teams/${abbr}/roster`)
      .then((data) => {
        if (cancelled) return;
        // The endpoint answers 200 with {"error":"unavailable"} when the
        // upstream fetch fails, so a successful HTTP status is not enough —
        // an absent athletes array is the real signal.
        const projected = projectRoster(data);
        if (!projected.players.length) {
          setError(true);
        } else {
          setRoster(projected);
        }
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [abbr]);

  return { roster, loading, error };
}

/**
 * One team's whole schedule, chronological — backs the team schedule list
 * page (DSPRT-43). A plain fetch rather than useScoreboard's SSE-fed one:
 * this is a season's worth of mostly-finished games, not tonight's live
 * scores, so there is nothing to stream — a page revisit just re-fetches,
 * same as useRoster.
 */
export function useTeamSchedule(abbr: string | null, /** A page about the results themselves: never masked while scores are hidden. */ results = false): {
  games: DisplayGame[];
  loading: boolean;
} {
  const [games, setGames] = useState<DisplayGame[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!abbr) {
      setGames([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);

    get<Game[]>(`/teams/${encodeURIComponent(abbr)}/schedule`)
      .then((data) => {
        if (cancelled) return;
        setGames(data.map(adapt));
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setGames([]);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [abbr]);

  return { games: useMasked(games, !results), loading };
}

/**
 * One player's profile, fetched on demand.
 *
 * `id` null means "no profile open" — same contract as useBoxScore and
 * useRoster: the hook clears, skips the request, and leaves nothing stale
 * behind when the modal closes.
 *
 * No projection step here, unlike useRoster. This endpoint is already
 * projected server-side (see the Player record), which is where that work
 * belongs and where the roster's ought to move: the profile arrives as ~7KB
 * of exactly the fields below, out of ~860KB of upstream payloads.
 *
 * `error` covers both a 404 for an id ESPN has never heard of and an upstream
 * failure, because the modal says the same thing either way — there is no
 * useful difference to a reader looking at a name that won't open.
 */
export function usePlayer(id: string | null): {
  player: PlayerProfile | null;
  loading: boolean;
  error: boolean;
} {
  const [player, setPlayer] = useState<PlayerProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!id) {
      setPlayer(null);
      setError(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    // Cleared for the same reason useBoxScore clears: hopping from one player
    // to another would otherwise show the previous man's career table under
    // the new man's name, which reads as wrong data rather than as loading.
    setPlayer(null);

    // /bio, not the bundled /players/{id} — see usePlayerCareer and
    // usePlayerGameLog for the other two pieces a player modal now fetches
    // in parallel instead of waiting on one call for all three (DSPRT-49's
    // progressive-loading fix). This is also the fastest of the three, so
    // it's the right one for id resolution to hang its 404 check on.
    get<PlayerProfile>(`/players/${encodeURIComponent(id)}/bio`)
      // A G League-only player (DSPRT-474): the NBA's athlete endpoint has
      // never heard of him, the G League's has his bio. A light profile.
      .catch(() => fetch(`${GL_API}/players/${encodeURIComponent(id)}`).then((r) => (r.ok ? (r.json() as Promise<PlayerProfile>) : Promise.reject(new Error(String(r.status))))))
      .then((data) => {
        if (cancelled) return;
        setPlayer(data);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  return { player, loading, error };
}

/**
 * The career averages table alone — fetched in parallel with usePlayer's
 * bio and usePlayerGameLog's current-season log, each painting its own
 * section of the player modal as soon as IT resolves rather than all three
 * gating one big spinner (DSPRT-49). `enabled` should be `!!id`-shaped —
 * there is nothing to fetch before a player id is actually known.
 */
export function usePlayerCareer(
  id: string | null,
  enabled: boolean,
): { career: PlayerCareerTable | null; loading: boolean } {
  const [career, setCareer] = useState<PlayerCareerTable | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!id || !enabled) {
      setCareer(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setCareer(null);

    // `rev` works like the career-leaders one: ignored by the backend, it
    // only moves browsers holding a 6-hour cached table onto a changed one.
    // Bumped for the Basketball-Reference season tables (legends' ABA years,
    // Wilt Chamberlain's 14 seasons).
    get<PlayerCareerTable>(`/players/${encodeURIComponent(id)}/career?rev=2`)
      .then((data) => {
        if (cancelled) return;
        setCareer(data);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setCareer({ columns: [], seasons: [], totals: [] });
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, enabled]);

  return { career, loading };
}

const NO_CAREER_TABLE: PlayerCareerTable = { columns: [], seasons: [], totals: [] };

/**
 * His season-by-season totals and playoff tables (DSPRT-337) — fetched in
 * parallel with the rest of the profile, like usePlayerCareer, so the career
 * section's switches know what they can offer before one is pressed.
 *
 * A request that fails is `failed`, not three empty tables: empty means he
 * has no such games, and the modal says so by leaving a switch out. `retry`
 * asks again (DSPRT-357).
 */
export function usePlayerTotals(
  id: string | null,
  enabled: boolean,
): { totals: PlayerTotals | null; loading: boolean; failed: boolean; retry: () => void } {
  const [totals, setTotals] = useState<PlayerTotals | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!id || !enabled) {
      setTotals(null);
      setLoading(false);
      setFailed(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    setTotals(null);

    get<PlayerTotals>(`/players/${encodeURIComponent(id)}/totals`)
      .then((data) => {
        if (cancelled) return;
        setTotals({
          regular: data.regular ?? NO_CAREER_TABLE,
          postseason: data.postseason ?? NO_CAREER_TABLE,
          postseasonAverages: data.postseasonAverages ?? NO_CAREER_TABLE,
        });
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setFailed(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, enabled, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { totals, loading, failed, retry };
}

/**
 * The single-game career best for PTS, REB, AST, BLK and STL — fetched in
 * parallel with the rest of the profile, same shape as usePlayerCareer.
 * `enabled` should be `!!id`-shaped, same reason as that hook.
 */
export function useCareerHighs(
  id: string | null,
  enabled: boolean,
): { careerHighs: CareerHighs | null; loading: boolean } {
  const [careerHighs, setCareerHighs] = useState<CareerHighs | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!id || !enabled) {
      setCareerHighs(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setCareerHighs(null);

    get<CareerHighs>(`/players/${encodeURIComponent(id)}/career-highs`)
      .then((data) => {
        if (cancelled) return;
        setCareerHighs(data);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setCareerHighs({ highs: [] });
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, enabled]);

  return { careerHighs, loading };
}

/**
 * Turns a player's URL slug into the id {@link usePlayer} takes, for a
 * profile opened cold from /player/{slug} — a bookmark, a shared link, a
 * typed-in URL. A no-op when `slug` is null, which is the common case: the
 * profile usually already knows its id from the click that opened it (see
 * PlayerModal's `hint`), and this hook only runs when it doesn't.
 *
 * `current` says whether the match came off an active roster or only from
 * ESPN's search index — see NbaController#resolvePlayerSlug. PlayerModal
 * reads that to decide the profile's colours: a club's own for `current`,
 * a neutral grey/black card for a real player search found who just isn't on
 * anyone's roster right now. `notFound` means neither path matched anything.
 */
export function useResolvePlayerId(slug: string | null): {
  id: string | null;
  current: boolean;
  loading: boolean;
  notFound: boolean;
} {
  const [id, setId] = useState<string | null>(null);
  const [current, setCurrent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) {
      setId(null);
      setCurrent(false);
      setLoading(false);
      setNotFound(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setId(null);
    setCurrent(false);

    // A name the NBA's rosters and index don't know may be a G League player's (DSPRT-474).
    // Both are asked at once (GL-22): one after the other, a name neither knows waited for two answers in a row.
    const nbaAsk = get<{ id: string; current: boolean }>(`/players/resolve/${encodeURIComponent(slug)}`);
    // A name the G League doesn't know is a 204 (B-300, as B-111 did for the fold): the usual answer for an NBA
    // player, so it should not log an error. An old 404, or an answer with no id, is the same miss.
    const glAsk = fetch(`${GL_API}/players/resolve/${encodeURIComponent(slug)}`)
      .then((r) => (r.ok && r.status !== 204 ? (r.json() as Promise<{ id?: string | null }>) : Promise.reject(new Error(String(r.status)))))
      .then((d) => (d?.id ? { id: d.id, current: true } : Promise.reject(new Error("no G League player"))));
    glAsk.catch(() => {});
    nbaAsk.catch(() => glAsk)
      .then((data) => {
        if (cancelled) return;
        setId(data.id);
        setCurrent(data.current);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setNotFound(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  return { id, current, loading, notFound };
}

/**
 * usePlayer, but falls back to slug resolution when the id it was given
 * doesn't pan out — or when there wasn't one to begin with.
 *
 * A profile opened from the draft page starts with the draft feed's own
 * athlete id, a DIFFERENT id space from /players/{id} (see DraftPick.playerId
 * server-side). It resolves for the rare pick whose two ids happen to agree,
 * and 404s for everyone else — including a player who is very much on a
 * roster right now. Treating that 404 as final is what used to send an active
 * player straight to the generic fallback; this retries by slug (the same
 * resolver a cold /player/:slug load uses — a roster scan, then ESPN's
 * search index for anyone that misses) before giving up.
 *
 * `retired` distinguishes a real profile the search fallback found from one
 * confirmed by a roster: PlayerModal reads it to skip the club's own colours
 * for someone not actually on a roster, however current their stat line
 * looks. Always false through the `knownId` fast path — that id came from a
 * roster or a box score to begin with, so there is nothing to distinguish.
 */
export function usePlayerResolved(
  knownId: string | null,
  slug: string,
): { player: PlayerProfile | null; loading: boolean; error: boolean; id: string | null; retired: boolean } {
  const known = usePlayer(knownId);
  const needsResolve = !knownId || known.error;
  const resolved = useResolvePlayerId(needsResolve ? slug : null);
  // Only worth a second fetch when it would ask for a DIFFERENT id than the
  // one that already failed.
  const fallbackId = needsResolve && resolved.id && resolved.id !== knownId ? resolved.id : null;
  const fallback = usePlayer(fallbackId);

  if (known.player) {
    // `knownId` succeeding is not the same as "on a current roster" — the
    // All-Time leaders list (DSPRT-44) hands PlayerModal every career
    // leader's real id up front, Kobe Bryant included, and that id resolves
    // here exactly as cleanly as an active player's does (see
    // PlayerProfile.active). Without this, only the SLUG fallback path below
    // ever set `retired`, so a legend opened from a box score's own id (a
    // roster hint, always current) came out grey and teamless like it
    // should, but the exact same legend opened from All-Time — where the id
    // was never in doubt — kept his last team's crest and colours forever.
    return { player: known.player, loading: false, error: false, id: knownId, retired: !known.player.active };
  }
  if (!needsResolve) {
    return { player: null, loading: known.loading, error: false, id: knownId, retired: false };
  }
  if (resolved.loading || (fallbackId && fallback.loading)) {
    return { player: null, loading: true, error: false, id: fallbackId ?? knownId, retired: false };
  }
  if (fallback.player) {
    // `=== false`, not `!resolved.current`: the resolve endpoint is cached
    // publicly for 6 hours (see NbaController#resolvePlayerSlug), and this
    // `current` field is new — a browser that fetched a given slug before
    // that field existed can still be serving the old cached body for up to
    // 6 hours after this deploys, where `current` is simply absent rather
    // than false. `!undefined` reads as retired and greys out an active
    // player's card; treating only an EXPLICIT false that way means a stale
    // cache just falls back to the safe old default (a normal, team-coloured
    // profile) instead of a wrong one.
    return { player: fallback.player, loading: false, error: false, id: fallbackId, retired: resolved.current === false };
  }
  // Not an answer yet, so not an error. Both lookups start out "not loading"
  // for the render before their effects run, and again for the render after
  // the slug resolves and before the profile fetch for that id has begun.
  // Read as a failure, either one closed PlayerModal (it closes itself on
  // `error`) before any profile opened from a /player/ link could load. Only
  // a slug the server does not know, or a failed profile fetch for the id it
  // gave, is a real error.
  const resolveSettled = resolved.notFound || !!resolved.id;
  const fallbackSettled = !fallbackId || fallback.error;
  if (slug && (!resolveSettled || !fallbackSettled)) {
    return { player: null, loading: true, error: false, id: fallbackId ?? knownId, retired: false };
  }
  return { player: null, loading: false, error: true, id: fallbackId ?? knownId, retired: false };
}

/**
 * The draft years the UI may offer, newest first. Fetched once.
 *
 * Comes from the server rather than being generated in the browser so the
 * floor stays tied to what ESPN actually serves (2001) in one place.
 */
export function useDraftYears(enabled = true): number[] {
  const [years, setYears] = useState<number[]>([]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    // v=2: the list reaches back to 1947 (DSPRT-310); browsers kept the 2001-on list for twelve hours.
    get<number[]>("/draft/years?v=2")
      .then((r) => !cancelled && setYears(r))
      .catch(() => !cancelled && setYears([]));
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return years;
}

/**
 * One draft's picks, in overall order.
 *
 * Both rounds arrive together — a draft is ~60 rows and splitting it per
 * round would double the requests to save nothing — so the round tabs and
 * the team filter both narrow this list in the browser.
 */
/** A pick's career, from the bundled careers file (DSPRT-334). Mirrors ColdDataService.DraftCareer. */
export interface DraftCareer {
  round: number;
  pick: number;
  overall: number | null;
  /** Plainly spelt, lower case: "olajuwon". */
  surname: string;
  seasons: number | null;
  games: number;
  pts: number | null;
  reb: number | null;
  ast: number | null;
  winShares: number | null;
}

/**
 * A draft class's careers; `loaded` once the answer is in, empty or not.
 * `asOf` is the day the snapshot was taken ("2026-10-12"), null if the
 * server doesn't say: it once answered with the bare list.
 */
export function useDraftCareers(year: number | null): { careers: DraftCareer[]; asOf: string | null; loaded: boolean; failed: boolean } {
  // `failed`: the request did not answer, so an empty list is "not known", not "nobody has played" (B-278).
  const [state, setState] = useState<{ year: number; careers: DraftCareer[]; asOf: string | null; failed?: boolean } | null>(null);
  useEffect(() => {
    if (year == null) return;
    let cancelled = false;
    get<DraftCareer[] | { asOf: string | null; careers: DraftCareer[] }>(`/draft/careers?year=${year}`)
      .then((body) => {
        if (cancelled) return;
        setState(Array.isArray(body) ? { year, careers: body, asOf: null } : { year, careers: body.careers ?? [], asOf: body.asOf ?? null });
      })
      .catch(() => { if (!cancelled) setState({ year, careers: [], asOf: null, failed: true }); });
    return () => { cancelled = true; };
  }, [year]);
  const hit = state?.year === year;
  return { careers: hit ? state!.careers : [], asOf: hit ? state!.asOf : null, loaded: hit, failed: hit && !!state!.failed };
}

export function useDraft(year: number | null): {
  picks: DraftPick[];
  loading: boolean;
  error: boolean;
} {
  const [picks, setPicks] = useState<DraftPick[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (year == null) {
      setPicks([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    // Cleared so switching year can't show last year's board under the new
    // year's heading while the request is in flight.
    setPicks([]);

    // Versioned because a past year is cached in the browser for a week: v2
    // when picks gained athleteId (DSPRT-58), v3 for DSPRT-64's photos and
    // corrected ids. Bump it whenever draft-athletes.json changes.
    // v5: the drafts before 2001 gained their later rounds, positions and heights (DSPRT-320).
    // v6: and the profiles of the picks ESPN files under another name (DSPRT-362).
    get<DraftPick[]>(`/draft?year=${year}&v=6`)
      .then((rows) => {
        if (cancelled) return;
        setPicks(rows);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [year]);

  return { picks, loading, error };
}

/**
 * This season's top five in each of six categories, fetched once.
 *
 * One call to an endpoint that does the sorting upstream, same as
 * useTeamStatLeaders below — the earlier version of team leaders fanned out
 * over a live roster client-side instead, which is also why it could never
 * show a player traded away mid-season. Gated on `enabled` the same way
 * useFranchises is: only the Leaders page wants it.
 */
/** One club's row of the team rankings: points scored, allowed and the margin, a game. */
export interface ClubRanking { abbr: string; pts: number; allowed: number; margin: number }
export interface TeamRankings { season: number; label: string; clubs: ClubRanking[] }

/**
 * All 30 clubs' points, points allowed and margin a game (DSPRT-366), for
 * the Leaders page's Clubs table. `season` is ESPN's year; null asks for
 * the newest season every club has played in.
 */
export function useTeamRankings(enabled: boolean, season: number | null): {
  data: TeamRankings | null; loading: boolean; error: boolean;
} {
  const [state, setState] = useState<{ key: string; data: TeamRankings | null; error: boolean } | null>(null);
  const key = `${season ?? ""}`;
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    get<TeamRankings>(season ? `/team-rankings?season=${season}` : "/team-rankings")
      .then((data) => { if (!cancelled) setState({ key, data, error: !data?.clubs?.length }); })
      .catch(() => { if (!cancelled) setState({ key, data: null, error: true }); });
    return () => { cancelled = true; };
  }, [enabled, season, key]);
  const mine = state && state.key === key ? state : null;
  return { data: mine?.data ?? null, loading: enabled && !mine, error: !!mine?.error };
}

export function useSeasonLeaders(enabled = true, season: number | null = null): {
  blocks: SeasonLeaderBlock[];
  /** The season actually shown — the newest with numbers when none was asked for. */
  season: number | null;
  firstSeason: number | null;
  latestSeason: number | null;
  loading: boolean;
  error: boolean;
} {
  // Each answer is kept with the season it was asked for, so the first frame
  // for another season is its skeleton, never an empty board or the last
  // season's numbers (Q696-03). The bounds carry over: the stepper keeps focus.
  const key = enabled ? String(season ?? "") : null;
  const [answer, setAnswer] = useState<{ key: string; data: SeasonLeadersPage | null; error: boolean } | null>(null);
  const [bounds, setBounds] = useState<{ first: number; latest: number } | null>(null);

  useEffect(() => {
    if (key == null) return;
    let cancelled = false;

    get<SeasonLeadersPage>(season ? `/season-leaders?season=${season}` : "/season-leaders")
      .then((page) => {
        if (cancelled) return;
        // The endpoint omits a category whose upstream call failed rather
        // than failing the whole response, so a page with no categories at
        // all is the failure. A page whose categories are all empty is a
        // season no games have been played into yet, which is not a fault on
        // our side and must not be reported as one (B-227).
        const all = page?.blocks ?? [];
        const blocks = all.filter((b) => b.leaders.length > 0);
        setAnswer({ key, data: { ...page, blocks }, error: all.length === 0 });
        if (page?.firstSeason && page?.latestSeason) setBounds({ first: page.firstSeason, latest: page.latestSeason });
      })
      .catch(() => {
        if (cancelled) return;
        setAnswer({ key, data: null, error: true });
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const mine = answer?.key === key ? answer : null;
  return {
    blocks: mine?.data?.blocks ?? [],
    season: mine?.data?.season ?? null,
    firstSeason: bounds?.first ?? null,
    latestSeason: bounds?.latest ?? null,
    loading: key != null && !mine,
    error: !!mine?.error,
  };
}

interface SeasonLeadersPage {
  season: number;
  firstSeason: number;
  latestSeason: number;
  blocks: SeasonLeaderBlock[];
}

/** One team's own leader in a single stat category — see useTeamStatLeaders. */
export interface TeamLeaderEntry {
  category: string;
  playerId: string;
  name: string;
  /** null when ESPN has no headshot for them — render initials instead. */
  headshotUrl: string | null;
  /** ESPN's own formatting, e.g. "17.0" or "44.8". */
  value: string;
  /** ESPN's season year it is from: 2026 = 2025-26. */
  season?: number;
  /** The games behind the average, sent for the live season only (NBA-11). */
  games?: number | null;
}

/**
 * A team's own leader in each of the given categories (e.g.
 * ["PTS","REB","AST","STL","BLK"]) — one call to a backend endpoint that
 * reads it off ESPN's own team-scoped leaderboard, which is computed
 * against stats actually earned for this team. Unlike a live roster scan,
 * that means a player traded or waived away mid-season still shows up here
 * if their run before leaving was good enough — the whole reason this
 * replaced the roster-fanout version of the hook.
 *
 * `categories` should be a stable reference (module-level array or memoed) —
 * it drives the effect's dependency array by identity, and a fresh array
 * literal on every render would refetch every render.
 */
export function useTeamStatLeaders(
  abbr: string | null,
  categories: readonly string[],
  /** ESPN season year; null = this season, or last if none of it is played yet. */
  season: number | null = null,
): { leaders: TeamLeaderEntry[] | null; loading: boolean } {
  const [leaders, setLeaders] = useState<TeamLeaderEntry[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!abbr || categories.length === 0) {
      setLeaders(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);

    const params = new URLSearchParams();
    for (const c of categories) params.append("categories", c);
    if (season) params.set("season", String(season));

    get<TeamLeaderEntry[]>(`/teams/${encodeURIComponent(abbr)}/leaders?${params}`)
      .then((rows) => {
        if (cancelled) return;
        setLeaders(rows);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLeaders([]);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [abbr, categories, season]);

  return { leaders, loading };
}

/** A player's newest trade or signing on the wire after a date (DSPRT-265). */
/** `from`: the club abroad he came from, when he hadn't played in the NBA for over a season (DSPRT-265). */
/** `kind` and `date` are missing when the wire has no entry for the move but his club abroad is known (DSPRT-268). */
export interface PlayerMove { kind?: "trade" | "signing"; date?: string; from?: string }

/**
 * The move behind a club change the last games list can see but not explain:
 * newer than `after` (his last game with the old club). null while loading or
 * when there's nothing to look up; `move` null when the wire has nothing.
 */
export function usePlayerMove(id: string | null, after: string | null, last: string | null = null,
  before: string | null = null): { move: PlayerMove | null; loading: boolean } {
  const [state, setState] = useState<{ key: string; move: PlayerMove | null } | null>(null);
  const key = id && after ? `${id}|${after}|${last ?? ""}|${before ?? ""}` : null;
  useEffect(() => {
    if (!key || !id || !after) return;
    let cancelled = false;
    const q = `after=${encodeURIComponent(after.slice(0, 10))}${last ? `&last=${encodeURIComponent(last.slice(0, 10))}` : ""}`
      + (before ? `&before=${encodeURIComponent(before.slice(0, 10))}` : "");
    get<Partial<PlayerMove>>(`/players/${encodeURIComponent(id)}/move?${q}`)
      .then((m) => { if (!cancelled) setState({ key, move: (m?.kind && m.date) || m?.from ? (m as PlayerMove) : null }); })
      .catch(() => { if (!cancelled) setState({ key, move: null }); });
    return () => { cancelled = true; };
  }, [key, id, after, last, before]);
  if (!key) return { move: null, loading: false };
  return state?.key === key ? { move: state.move, loading: false } : { move: null, loading: true };
}

/** One award, and every year the player won it — see usePlayerAwards. */
export interface PlayerAwardEntry {
  name: string;
  years: number[];
}

/**
 * A player's whole award history, grouped by award name — one call to a
 * backend endpoint that walks ESPN's per-athlete awards list and resolves
 * each season+award pair, then groups repeats ("All-NBA First Team" six
 * different years) into a single entry instead of six rows.
 *
 * First load can be slow server-side (a new award-year pair costs its own
 * upstream call), so this can stay `loading` noticeably longer than the
 * other player-modal hooks — worth a distinct empty/loading state in the UI
 * rather than treating a long wait as a failure.
 */
export function usePlayerAwards(id: string | null): {
  awards: PlayerAwardEntry[] | null;
  loading: boolean;
} {
  const [awards, setAwards] = useState<PlayerAwardEntry[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!id) {
      setAwards(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setAwards(null);

    get<{ awards: PlayerAwardEntry[] }>(`/players/${encodeURIComponent(id)}/awards`)
      .then((data) => {
        if (cancelled) return;
        setAwards(data.awards ?? []);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setAwards([]);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  return { awards, loading };
}

/**
 * A player's whole season game log — see the "Game Logs" button on the
 * player modal's Last Games table (DSPRT-49), which is the only place this
 * is used. Deliberately gated by `enabled` rather than firing whenever `id`
 * is set: the profile's own table already covers the common case (the last
 * ten), so this is fetched only once someone actually asks to see the rest.
 */
/**
 * @param season ESPN's own convention (the year it ends in, e.g. 2024 for
 *               2023-24) — null/undefined means the current season. Backs
 *               GameLogModal's year filter; changing it refetches.
 */
export function usePlayerGameLog(
  id: string | null,
  enabled: boolean,
  season?: number | null,
): { gameLog: PlayerGameLog | null; loading: boolean } {
  const [gameLog, setGameLog] = useState<PlayerGameLog | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!id || !enabled) {
      setGameLog(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setGameLog(null);

    const q = season ? `?season=${season}` : "";
    get<PlayerGameLog>(`/players/${encodeURIComponent(id)}/gamelog${q}`)
      .then((data) => {
        if (cancelled) return;
        setGameLog(data);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setGameLog({ columns: [], games: [] });
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, enabled, season]);

  return { gameLog, loading };
}

/**
 * His last ten games whatever season they're in (DSPRT-153) — the player
 * card's Last Games. A separate call from usePlayerGameLog: that one is a
 * single season, for the Game Logs modal.
 */
export function useRecentGames(id: string | null): { gameLog: PlayerGameLog | null; loading: boolean } {
  const [gameLog, setGameLog] = useState<PlayerGameLog | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setGameLog(null);
    if (!id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    get<PlayerGameLog>(`/players/${encodeURIComponent(id)}/gamelog/recent`)
      .then((data) => {
        if (cancelled) return;
        setGameLog(data);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setGameLog({ columns: [], games: [] });
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  return { gameLog, loading };
}

/** One player-search result. Mirrors ColdDataService.PlayerHit. */
export interface PlayerSearchHit {
  id: string;
  name: string;
  teamAbbr: string | null;
  position: string | null;
  jersey: string | null;
  headshotUrl: string | null;
  /** Null on a current roster; "Free agent" or "Retired" otherwise. */
  status: string | null;
  /** Career span for a non-rostered player, e.g. "1984–2003". */
  years: string | null;
}

/**
 * A position worth printing. "NA" and "Not Available" are ESPN's placeholder
 * for a player it has no position for, not a position (B-181): older legends
 * carry it, and filter(Boolean) kept it because the string isn't empty.
 */
export function realPosition(position: string | null | undefined): string | null {
  return position && !/^(not available|na)$/i.test(position.trim()) ? position : null;
}

/** A search result's second line: club, position and number, or status and career years. */
export function playerHitSub(h: PlayerSearchHit): string {
  const position = realPosition(h.position);
  const parts = h.status
    ? [h.status, position, h.years]
    : [h.teamAbbr, position, h.jersey ? `#${h.jersey}` : null];
  return parts.filter(Boolean).join(" · ");
}

/**
 * Players matching what has been typed so far: current rosters, then free
 * agents and retired players.
 *
 * Debounced, and a stale response is dropped rather than shown: a slow answer
 * for "lebr" must not overwrite the results for "lebron". Under two characters
 * there is nothing to ask, so it returns an empty list without a request.
 */
export function usePlayerSearch(query: string): { hits: PlayerSearchHit[]; loading: boolean; failed: boolean } {
  const q = query.trim();
  const [hits, setHits] = useState<PlayerSearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  // Kept apart from an empty result: "nobody matches" and "the search did not
  // answer" need different words, or a failure reads as a player not existing.
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
    if (q.length < 2) {
      setHits([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      get<PlayerSearchHit[]>(`/players/search?q=${encodeURIComponent(q)}`)
        .then((data) => { if (!cancelled) setHits(data); })
        .catch(() => { if (!cancelled) { setHits([]); setFailed(true); } })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 160);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  return { hits, loading, failed };
}

/**
 * One day's games as a plain fetch, with no live stream: the home page's
 * "last night", which is always a finished day. `loaded` turns true once the
 * answer is in, empty or not, so the caller can tell "no games" from "not yet".
 */
export function useGamesOn(date: string | null, /** As for useTeamSchedule: never masked. */ results = false): { games: DisplayGame[]; loaded: boolean } {
  const [state, setState] = useState<{ date: string | null; games: DisplayGame[] }>({ date: null, games: [] });
  useEffect(() => {
    if (!date) return;
    let cancelled = false;
    get<Game[]>(`/scoreboard?date=${date}&v=${PAST_DAY_VERSION}`)
      .then((rows) => { if (!cancelled) setState({ date, games: rows.filter((g) => !isUndecided(g)).map(adapt) }); })
      .catch(() => { if (!cancelled) setState({ date, games: [] }); });
    return () => { cancelled = true; };
  }, [date]);
  return { games: useMasked(state.date === date ? state.games : NO_GAMES, !results), loaded: !!date && state.date === date };
}

/**
 * Scheduled games in a date range, optionally one season type
 * ("preseason", "regular-season", "post-season"). Served from Postgres by
 * /schedule, never from ESPN. `enabled` false skips the request.
 */
export function useUpcoming(
  from: string,
  to: string,
  seasonType: string | null,
  enabled = true,
  /** Only the first this-many days with games in the window (DSPRT-237): opening night and week one, not five months. */
  days?: number,
): { games: DisplayGame[]; loaded: boolean } {
  const key = `${from}|${to}|${seasonType ?? ""}|${days ?? ""}`;
  const [state, setState] = useState<{ key: string; games: DisplayGame[] } | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const q = new URLSearchParams({ from, to });
    if (seasonType) q.set("seasonType", seasonType);
    if (days) q.set("days", String(days));
    get<Game[]>(`/schedule?${q}`)
      // The home page's lists are of games someone can look forward to by name: a slot still to be decided waits on Scores (#123).
      .then((rows) => { if (!cancelled) setState({ key, games: rows.filter((g) => !isUndecided(g)).map(adapt) }); })
      .catch(() => { if (!cancelled) setState({ key, games: [] }); });
    return () => { cancelled = true; };
  }, [key, enabled, from, to, seasonType, days]);
  const hit = state?.key === key;
  return { games: useMasked(hit ? state!.games : NO_GAMES), loaded: hit };
}

/** One injured player, from /injuries. Mirrors ColdDataService.InjuryLine. */
export interface InjuryLine {
  teamAbbr: string;
  athleteId: string;
  name: string;
  status: string;
  bodyPart: string | null;
  side: string | null;
  /** His points a game and the season they are from (DSPRT-343); absent from a server that doesn't send them, null for a player with no line. */
  ppg?: number | null;
  ppgSeason?: string | null;
}

/**
 * Current injuries for the given teams, keyed by abbreviation, most serious first.
 * `failed` when the request did not answer: an empty `byTeam` then means "not
 * known", never "nobody is hurt" (B-270), so a caller says it couldn't load.
 */
export function useInjuries(teams: string[]): { byTeam: Record<string, InjuryLine[]>; loaded: boolean; failed: boolean } {
  const key = [...new Set(teams)].sort().join(",");
  const [state, setState] = useState<{ key: string; byTeam: Record<string, InjuryLine[]>; failed: boolean } | null>(null);
  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    get<Record<string, InjuryLine[]>>(`/injuries?teams=${encodeURIComponent(key)}`)
      .then((d) => { if (!cancelled) setState({ key, byTeam: d, failed: false }); })
      .catch(() => { if (!cancelled) setState({ key, byTeam: {}, failed: true }); });
    return () => { cancelled = true; };
  }, [key]);
  const hit = state?.key === key;
  return { byTeam: hit ? state!.byTeam : {}, loaded: hit, failed: hit && state!.failed };
}

/** A team's form going into a day. Mirrors ScoreboardService.TeamForm. */
export interface TeamForm {
  abbr: string;
  /** "W" or "L": the kind of the current streak. */
  result: "W" | "L";
  length: number;
  /** Last ten results, oldest first, e.g. "LWLWWWWWWW". */
  last10: string;
  wins: number;
  losses: number;
}

/** Every team's streak and last ten going into `asOf`. */
export function useTeamForm(asOf: string | null): TeamForm[] {
  const [state, setState] = useState<{ asOf: string; rows: TeamForm[] } | null>(null);
  useEffect(() => {
    if (!asOf) return;
    let cancelled = false;
    get<TeamForm[]>(`/form?asOf=${asOf}`)
      .then((rows) => { if (!cancelled) setState({ asOf, rows }); })
      .catch(() => { if (!cancelled) setState({ asOf, rows: [] }); });
    return () => { cancelled = true; };
  }, [asOf]);
  return state?.asOf === asOf ? state.rows : [];
}


/**
 * One club's payroll (DSPRT-111) — the team modal's Payroll section. Shared
 * per club for the page's lifetime, so opening the same team's modal twice
 * costs no second fetch. A failed request is dropped from the cache so the
 * next open tries again.
 *
 * The player modal's own Contract section reads a different, smaller
 * endpoint instead (see usePlayerContracts) — one row, or two for a player
 * still owed dead money elsewhere, rather than a whole roster's worth.
 */
const contractRequests = new Map<string, Promise<TeamContracts | null>>();

/**
 * Exported for the Trade Machine, which reads up to four clubs at once. One
 * retry: a burst of cold payroll reads has answered 500 on prod and then
 * succeeded a second later.
 */
export function teamContracts(abbr: string): Promise<TeamContracts | null> {
  let req = contractRequests.get(abbr);
  if (!req) {
    const path = `/teams/${encodeURIComponent(abbr)}/contracts`;
    req = get<TeamContracts>(path).catch(() =>
      new Promise<void>((r) => setTimeout(r, 1200)).then(() => get<TeamContracts>(path)),
    ).catch(() => {
      contractRequests.delete(abbr);
      return null;
    });
    contractRequests.set(abbr, req);
  }
  return req;
}

export function useTeamContracts(abbr: string | null): { contracts: TeamContracts | null; loading: boolean } {
  const [contracts, setContracts] = useState<TeamContracts | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setContracts(null);
    if (!abbr) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    teamContracts(abbr).then((data) => {
      if (cancelled) return;
      setContracts(data);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [abbr]);

  return { contracts, loading };
}

/**
 * Every contract one player has (DSPRT-115): his current deal, plus any
 * dead money a club still owes him after waiving him — Bradley Beal (LAC +
 * PHO), Damian Lillard (POR + MIL). Ordered current-club-first by the
 * backend; empty for anyone with no current club to start the search from
 * (a free agent, a retiree), same as the plain single-contract case did.
 */
export function usePlayerContracts(id: string | null): { entries: PlayerContractEntry[]; loading: boolean } {
  const [entries, setEntries] = useState<PlayerContractEntry[]>([]);
  const [loading, setLoading] = useState(!!id);

  useEffect(() => {
    setEntries([]);
    if (!id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    let retry = 0;
    setLoading(true);
    const read = (again: boolean) =>
      get<PlayerContracts>(`/players/${encodeURIComponent(id)}/contracts`)
        .then((data) => {
          if (cancelled) return;
          setEntries(data.entries);
          setLoading(false);
          // A first read answers with his current deal while the server
          // still looks for dead money elsewhere (DSPRT-398): ask once more.
          if (data.partial && !again) retry = window.setTimeout(() => read(true), 4000);
        })
        .catch(() => {
          if (cancelled || again) return;
          setEntries([]);
          setLoading(false);
        });
    read(false);
    return () => {
      cancelled = true;
      window.clearTimeout(retry);
    };
  }, [id]);

  return { entries, loading };
}

/** One season's pay from Basketball-Reference — see usePlayerSalaries. */
export interface PlayerSalary {
  season: string;
  team: string;
  league: string;
  /** Null when BBRef prints a bracket instead ("< $Minimum"); `display` has it. */
  amount: number | null;
  display: string;
}

/**
 * A player's salary season by season (DSPRT-144): the contract history
 * modal's rows. Fetched only once that modal opens — it's a BBRef page read
 * the card itself never needs.
 */
export function usePlayerSalaries(id: string | null, enabled: boolean): { salaries: PlayerSalary[] | null; loading: boolean } {
  const [salaries, setSalaries] = useState<PlayerSalary[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSalaries(null);
    if (!id || !enabled) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    get<PlayerSalary[]>(`/players/${encodeURIComponent(id)}/salaries`)
      .then((rows) => {
        if (cancelled) return;
        setSalaries(Array.isArray(rows) ? rows : []);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setSalaries([]);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, enabled]);

  return { salaries, loading };
}

/**
 * Every rostered player with his recent per-game lines (DSPRT-132): what the
 * Trade Machine values players and ranks teams by. One request per page view.
 */
let tradeLeagueRequest: Promise<TradeLeague | null> | null = null;

export function useTradeLeague(enabled = true): { league: TradeLeague | null; failed: boolean } {
  const [state, setState] = useState<{ league: TradeLeague | null; failed: boolean }>({ league: null, failed: false });
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    if (!tradeLeagueRequest) {
      tradeLeagueRequest = get<TradeLeague>("/trade/league").catch(() => {
        tradeLeagueRequest = null;
        return null;
      });
    }
    tradeLeagueRequest.then((league) => { if (live) setState({ league, failed: !league }); });
    return () => { live = false; };
  }, [enabled]);
  return state;
}

/** A club's star on the home page's upcoming games (DSPRT-220): an admin's pick, else its best scorer. */
export interface TeamStarPick {
  id: string;
  name: string;
  headshotUrl: string | null;
  ppg: number | null;
  manual: boolean;
}

let teamStarsRequest: Promise<Record<string, TeamStarPick> | null> | null = null;

/** Every club's star, from a file of about 3 kB; null until it lands, and `failed` if it never does. */
export function useTeamStars(): { stars: Record<string, TeamStarPick> | null; failed: boolean } {
  const [state, setState] = useState<{ stars: Record<string, TeamStarPick> | null; failed: boolean }>({ stars: null, failed: false });
  useEffect(() => {
    let live = true;
    if (!teamStarsRequest) {
      teamStarsRequest = get<{ stars: Record<string, TeamStarPick> }>("/team-stars").then((d) => d.stars).catch(() => {
        teamStarsRequest = null;
        return null;
      });
    }
    teamStarsRequest.then((stars) => { if (live) setState({ stars, failed: !stars }); });
    return () => { live = false; };
  }, []);
  return state;
}

/**
 * Pick ownership and cap-sheet details for the Trade Machine (DSPRT-133): a
 * bundled snapshot, so one request per page view. The page works without it,
 * on Basketball-Reference's figures alone.
 */
let tradeSnapshotRequest: Promise<TradeSnapshot | null> | null = null;

export function useTradeSnapshot(): { snapshot: TradeSnapshot | null; done: boolean } {
  const [state, setState] = useState<{ snapshot: TradeSnapshot | null; done: boolean }>({ snapshot: null, done: false });
  useEffect(() => {
    let live = true;
    if (!tradeSnapshotRequest) {
      tradeSnapshotRequest = get<TradeSnapshot>("/trade/snapshot").then((s) => (s && s.picks ? s : null)).catch(() => {
        tradeSnapshotRequest = null;
        return null;
      });
    }
    tradeSnapshotRequest.then((snapshot) => { if (live) setState({ snapshot, done: true }); });
    return () => { live = false; };
  }, []);
  return state;
}

/** One of next summer's free agents (DSPRT-414). `kind` is unrestricted, restricted, or an option on next season. */
export interface FreeAgent {
  name: string;
  playerId: string | null;
  bbrefId: string;
  team: string;
  kind: "UFA" | "RFA" | "PO" | "TO";
  /** This season's salary. */
  salary: number;
  /** Next season's amount, for an option. */
  option: number | null;
  deal: string | null;
  signed: string | null;
  age: number | null;
  position: string | null;
  headshotUrl: string | null;
}

export interface FreeAgentClass {
  /** The summer they become free agents: 2027 for this season's payrolls. */
  summer: number;
  /** By this season's salary, highest first: a player's place here is his number on the page. */
  players: FreeAgent[];
  /** Clubs whose payroll the server has not read yet. */
  missing: string[];
  readAt: string | null;
}

/**
 * Next summer's free agents (DSPRT-414), read once per page view and shared:
 * the page, a club's Payroll line and a player's Contract all ask for it.
 */
let freeAgentRequest: Promise<FreeAgentClass | null> | null = null;

export function useFreeAgents(enabled = true): { fa: FreeAgentClass | null; done: boolean } {
  const [state, setState] = useState<{ fa: FreeAgentClass | null; done: boolean }>({ fa: null, done: false });
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    if (!freeAgentRequest) {
      freeAgentRequest = get<FreeAgentClass>("/free-agents").then((c) => (c && Array.isArray(c.players) ? c : null)).catch(() => {
        freeAgentRequest = null;
        return null;
      });
    }
    freeAgentRequest.then((fa) => { if (live) setState({ fa, done: true }); });
    return () => { live = false; };
  }, [enabled]);
  return state;
}

/**
 * Every champion since 1984 (DSPRT-136), a bundled file on the server, so one
 * request per page view. A failure is kept as `failed`, not retried in a loop.
 */
let championsRequest: Promise<Champion[] | null> | null = null;

/** A postseason's seeds by club, for the road to the title on the home champions banner (DSPRT-157). */
export function usePostseasonSeeds(season: number | null): Record<string, number> {
  const [seeds, setSeeds] = useState<Record<string, number>>({});
  useEffect(() => {
    if (!season) return;
    let live = true;
    get<{ seeds?: Record<string, number> }>(`/postseason?season=${season}`)
      .then((d) => { if (live) setSeeds(d?.seeds ?? {}); })
      .catch(() => {});
    return () => { live = false; };
  }, [season]);
  return seeds;
}

/** A club's retired numbers (DSPRT-187); null when the request failed. */
export function useRetiredNumbers(abbr: string): { banners: RetiredBanner[] | null; loading: boolean } {
  const [state, setState] = useState<{ banners: RetiredBanner[] | null; loading: boolean }>({ banners: null, loading: true });
  useEffect(() => {
    let live = true;
    setState({ banners: null, loading: true });
    get<{ banners: RetiredBanner[] }>(`/teams/${encodeURIComponent(abbr)}/retired-numbers`)
      .then((r) => { if (live) setState({ banners: r.banners ?? [], loading: false }); })
      .catch(() => { if (live) setState({ banners: null, loading: false }); });
    return () => { live = false; };
  }, [abbr]);
  return state;
}

let allRetiredRequest: Promise<Record<string, RetiredBanner[]> | null> | null = null;
/** Every club's retired numbers, one request for the whole visit (DSPRT-263). */
export function useAllRetiredNumbers(enabled = true): Record<string, RetiredBanner[]> | null {
  const [all, setAll] = useState<Record<string, RetiredBanner[]> | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    if (!allRetiredRequest) {
      allRetiredRequest = get<Record<string, RetiredBanner[]>>("/retired-numbers").catch(() => { allRetiredRequest = null; return null; });
    }
    allRetiredRequest.then((r) => { if (live) setAll(r); });
    return () => { live = false; };
  }, [enabled]);
  return all;
}

export function useChampions(enabled = true): { champions: Champion[]; loading: boolean; failed: boolean } {
  const [state, setState] = useState<{ champions: Champion[]; loading: boolean; failed: boolean }>({ champions: [], loading: true, failed: false });
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    if (!championsRequest) {
      championsRequest = get<Champion[]>("/champions").then((c) => (Array.isArray(c) ? c : null)).catch(() => {
        championsRequest = null;
        return null;
      });
    }
    championsRequest.then((c) => { if (live) setState({ champions: c ?? [], loading: false, failed: !c || c.length === 0 }); });
    return () => { live = false; };
  }, [enabled]);
  return state;
}

/* ------------------------------------------------------------------
 * Round twelve's lists (DSPRT-377..381): one read each, no polling.
 * ------------------------------------------------------------------ */

/** One GET, kept for the life of the component; `path` null skips it. */
function useRead<T>(path: string | null): { data: T | null; loaded: boolean; failed: boolean } {
  const [state, setState] = useState<{ path: string; data: T | null; failed: boolean } | null>(null);
  useEffect(() => {
    if (!path) return;
    let cancelled = false;
    get<T>(path)
      .then((data) => { if (!cancelled) setState({ path, data, failed: false }); })
      .catch(() => { if (!cancelled) setState({ path, data: null, failed: true }); });
    return () => { cancelled = true; };
  }, [path]);
  const hit = !!path && state?.path === path;
  return { data: hit ? state!.data : null, loaded: hit, failed: hit && state!.failed };
}

/** One pick of any draft class, as the by-country and club-history tables read it. Mirrors DraftIndexService.Row. */
export interface DraftIndexRow {
  year: number;
  round: number;
  overall: number;
  name: string;
  athleteId: string | null;
  teamAbbr: string | null;
  teamThen: string | null;
  position: string | null;
  school: string | null;
  country: string | null;
}

/**
 * Picks per birth country, as the profiles name it, with the athlete ids
 * behind each country except the USA (DSPRT-385, #131): what the Country menu
 * and the per-player corrections need, in place of every row of every class.
 */
export interface DraftTally { counts: Record<string, number>; ids: Record<string, string[]> }

export function useDraftTally(enabled: boolean): { tally: DraftTally | null; loaded: boolean; failed: boolean } {
  const { data, loaded, failed } = useRead<DraftTally>(enabled ? "/draft/tally" : null);
  return { tally: data, loaded, failed };
}

/** A school (or a club abroad) with five picks or more. */
export interface DraftSchool { school: string; picks: number }

export function useDraftSchools(enabled: boolean): { schools: DraftSchool[]; loaded: boolean } {
  const { data, loaded } = useRead<DraftSchool[]>(enabled ? "/draft/schools" : null);
  return { schools: data ?? NO_SCHOOLS, loaded };
}
const NO_SCHOOLS: DraftSchool[] = [];

/**
 * The all-years list (DSPRT-388): picks born in these countries (the feed's
 * own names), from this school, at this pick number, lowest pick first.
 * Null, or nothing to narrow by, asks for nothing.
 */
export function useDraftAll(ask: { countries?: string[]; school?: string; pick?: number | null; first?: number } | null):
    { rows: DraftIndexRow[]; loaded: boolean; failed: boolean } {
  const q = new URLSearchParams();
  if (ask?.countries?.length) q.set("countries", [...ask.countries].sort().join(","));
  if (ask?.school) q.set("school", ask.school);
  if (ask?.pick) q.set("pick", String(ask.pick));
  const any = [...q.keys()].length > 0;
  if (any && ask?.first) q.set("first", String(ask.first));
  const { data, loaded, failed } = useRead<DraftIndexRow[]>(any ? `/draft/all?${q}` : null);
  return { rows: data ?? NO_ROWS, loaded, failed };
}

/** One career on a country's all-time list (DSPRT-406). `approx`: the points are his career average times his games. Mirrors DraftIndexService.Career. */
export interface CountryCareer {
  id: string;
  name: string;
  games: number;
  points: number;
  approx: boolean;
  allStars: number;
  firstAllStar: number | null;
  drafted: number | null;
  overall: number | null;
}
export interface CountryCareers { rows: CountryCareer[]; firstPick: DraftIndexRow | null }

/**
 * A country's careers (DSPRT-406): the players the page knows were born there
 * (its picks, its players on a roster, its All-Stars), or with `usa` the US-born
 * players of the All-Time pool, the USA's picks being too many to send.
 */
export function useCountryCareers(ask: { ids: string[]; usa: boolean; iso?: string } | null): { careers: CountryCareers | null; loaded: boolean } {
  const q = new URLSearchParams();
  if (ask?.ids.length) q.set("ids", ask.ids.slice(0, 600).join(","));
  if (ask?.usa) q.set("usa", "true");
  if (ask?.iso && /^[a-z]{2}$/i.test(ask.iso)) q.set("country", ask.iso.toLowerCase());
  const { data, loaded } = useRead<CountryCareers>([...q.keys()].length ? `/careers?${q}` : null);
  return { careers: data, loaded };
}

/** Where each All-Star was born, by player id (DSPRT-389). */
export function useAllStarsBorn(enabled: boolean): { born: Record<string, string>; loaded: boolean } {
  const { data, loaded } = useRead<Record<string, string>>(enabled ? "/alltime/allstar/born" : null);
  return { born: data ?? NO_BORN, loaded };
}
const NO_BORN: Record<string, string> = {};

/** One season's selected teams (DSPRT-386): allnba, alldef, allrookie, each first team first. A selection not made that season is absent. */
export interface SeasonTeams { season: number; teams: Record<string, ListedPlayer[][]> }

export function useAllTeams(enabled: boolean): { seasons: SeasonTeams[]; loaded: boolean; failed: boolean } {
  const { data, loaded, failed } = useRead<{ seasons: SeasonTeams[] }>(enabled ? "/alltime/teams" : null);
  return { seasons: data?.seasons ?? NO_TEAMS, loaded, failed };
}
const NO_TEAMS: SeasonTeams[] = [];

/** One Hall of Fame induction (DSPRT-387): `as` is Player, Coach, Contributor, Referee or Team; `nba` when he has an NBA or ABA playing record. */
export interface HallInductee { name: string; id: string | null; as: string; nba: boolean }
export interface HallClass { year: number; people: HallInductee[] }

export function useHallOfFame(enabled: boolean): { classes: HallClass[]; loaded: boolean; failed: boolean } {
  const { data, loaded, failed } = useRead<{ classes: HallClass[] }>(enabled ? "/alltime/hof" : null);
  return { classes: data?.classes ?? NO_HALL, loaded, failed };
}
const NO_HALL: HallClass[] = [];
const NO_ROWS: DraftIndexRow[] = [];

/** Every pick filed under these club codes, every class: a franchise's draft history (DSPRT-381). */
export function useClubDraft(codes: string[] | null): { rows: DraftIndexRow[]; loaded: boolean; failed: boolean } {
  const key = codes && codes.length ? [...codes].sort().join(",") : null;
  const { data, loaded, failed } = useRead<DraftIndexRow[]>(key ? `/draft/club?teams=${key}` : null);
  return { rows: data ?? NO_ROWS, loaded, failed };
}

/** One rostered player and his birth country. Mirrors DraftIndexService.Rostered. */
export interface RosteredNative {
  id: string;
  name: string;
  teamAbbr: string;
  position: string | null;
  jersey: string | null;
  country: string | null;
}

/** Everyone on a current roster with his birth country (DSPRT-380). */
export function useLeagueBorn(enabled: boolean): { players: RosteredNative[]; loaded: boolean; failed: boolean } {
  const { data, loaded, failed } = useRead<RosteredNative[]>(enabled ? "/league/born" : null);
  return { players: data ?? NO_NATIVES, loaded, failed };
}
const NO_NATIVES: RosteredNative[] = [];

/** A name on an All-Star roster or an award: `id` is his player page's, null when the site has none for him. */
export interface ListedPlayer { name: string; id: string | null }

export interface AllStarSide {
  name: string;
  /** otherTeams.ts's key for the squad's crest ("EAST", "LEB"), null where it has none. */
  code: string | null;
  score: number;
  players: Array<ListedPlayer & { club: string | null; pts: number | null; starter: boolean }>;
}

/** One All-Star Game: the winner first. `final` is false for the earlier games of a year played as a small tournament. */
export interface AllStarGame {
  year: number;
  date: string;
  site: string;
  ot: boolean;
  final: boolean;
  mvp: ListedPlayer[];
  teams: [AllStarSide, AllStarSide];
}

/** Every All-Star Game, newest first (DSPRT-378). */
export function useAllStarGames(enabled: boolean): { games: AllStarGame[]; loaded: boolean; failed: boolean } {
  const { data, loaded, failed } = useRead<{ games: AllStarGame[] }>(enabled ? "/alltime/allstar" : null);
  return { games: data?.games ?? NO_ALLSTAR, loaded, failed };
}
const NO_ALLSTAR: AllStarGame[] = [];

/** One award in one season: the winner or winners (two shared Rookie of the Year three times) and the club's code then. */
export interface AwardWin { name: string; id: string | null; club: string | null }

export interface AwardSeason {
  /** The season's closing year: 2025 is 2024-25. */
  season: number;
  /** Keyed by award: mvp, roy, dpoy, smoy, mip, fmvp, coy. An award not given that season is absent. */
  awards: Record<string, AwardWin[]>;
}

/** Each season's award winners, newest first (DSPRT-379). */
export function useSeasonAwards(enabled: boolean): { seasons: AwardSeason[]; loaded: boolean; failed: boolean } {
  const { data, loaded, failed } = useRead<{ seasons: AwardSeason[] }>(enabled ? "/alltime/awards" : null);
  return { seasons: data?.seasons ?? NO_AWARDS, loaded, failed };
}
const NO_AWARDS: AwardSeason[] = [];
